import { useMemo, useEffect, useRef, useCallback } from 'react';
import { AsyncQueryStatus } from 'reporting-data/constants/snowflakeQuery';
import { asyncQuery, cancel } from 'reporting-snowflake/funnel/resolve/request';
import { sameDataRequirements } from 'reporting-snowflake/funnel/resolve/dataRequirements';
import { clampLimitAndOffset } from 'reporting-snowflake/funnel/utils/limitAndOffset';
import usePromiseEffect from '../../shared/hooks/usePromiseEffect';
import memoizeOne from 'react-utils/memoizeOne';
//@ts-expect-error Untyped Dependency
import { useDifferent } from '../../snowflake/internal/queryUtils';

/**
 * Hook to manage canceling a running query.
 * @param queryWithOpts Object containing journey query and report options
 * @returns a callback function to be used as the onTick in the query poller.
 */
const useAutoCancel = queryWithOpts => {
  const currentQuery = useRef(queryWithOpts);
  useEffect(() => {
    currentQuery.current = queryWithOpts;
  }, [queryWithOpts]);
  useEffect(() => {
    return () => {
      currentQuery.current = undefined;
    };
  }, []);
  return useCallback((asyncQueryResponseMeta, query) => {
    const {
      queryId,
      queryStatus
    } = asyncQueryResponseMeta;
    if (queryStatus === AsyncQueryStatus.RUNNING && !sameDataRequirements(currentQuery.current, query)) {
      cancel(queryId).catch(error => {
        console.error(`Error in useQuery, could not automatically cancel query ${queryId}`, error);
      });
      return true;
    }
    return false;
  }, []);
};
const useMemoizeReport = queryWithOpts => {
  const memoizeReport = useMemo(() => memoizeOne(query => query,
  //@ts-expect-error memoizeOne typing errors while code works
  sameDataRequirements), []);
  return memoizeReport(clampLimitAndOffset(queryWithOpts));
};

/**
 * Manages calling the asynchronous query poller. Should only re-query when the
 * data requirements of the query and options change.
 * @param queryWithOpts Object containing journey query and report options
 * @returns Async State that will resolve to the Dataset from the BE
 */
const useQuery = (queryWithOpts, resolveParameters, debug) => {
  const memoizedQuery = useMemoizeReport(queryWithOpts);
  const autoCancel = useAutoCancel(memoizedQuery);
  const {
    reportId
  } = resolveParameters;
  const fetch = useCallback(() => asyncQuery(memoizedQuery, {
    reportId,
    onTick: autoCancel
  }, debug), [memoizedQuery, reportId, autoCancel, debug]);
  const differentRequirements = useDifferent(memoizedQuery);
  const shouldRefetch = ({
    data
  }) => data ? differentRequirements : true;
  const queryState = usePromiseEffect(fetch, {
    enabledFn: shouldRefetch
  });
  return queryState;
};
export default useQuery;